import { useMemo, useState } from 'react';
import { logger } from '../../../shared/utils/logger.js';
import RoleEditorModal from '../components/RoleEditorModal';
import {
  useCreateEmployeeRole,
  useDeleteEmployeeRole,
  useEmployees,
  useEmployeeRoles,
  useToggleEmployeeRoleStatus,
  useUpdateEmployeeRole,
} from '../queries';
import type { EmployeeRoleRow, EmployeeRoleWriteInput } from '../api/employees.api';

type StatusFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';

type RolesTabProps = {
  active: boolean;
  onToast?: (toast: { type: 'success' | 'error'; message: string }) => void;
};

const MODULE = 'employees-roles-tab';

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  logger.info(MODULE, event, { event, ...fields });
};

const errorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const RolesTab = ({ active, onToast }: RolesTabProps) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeRoleRow | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<EmployeeRoleRow | null>(null);

  const filterParam = statusFilter === 'ALL' ? undefined : statusFilter;
  const rolesQuery = useEmployeeRoles(active, filterParam);
  const roleEmployeesQuery = useEmployees(
    active && Boolean(deleteCandidate?.roleUuid),
    deleteCandidate?.roleUuid ? { roleUuid: deleteCandidate.roleUuid } : {},
  );

  const createMutation = useCreateEmployeeRole();
  const updateMutation = useUpdateEmployeeRole();
  const statusMutation = useToggleEmployeeRoleStatus();
  const deleteMutation = useDeleteEmployeeRole();

  const submitting = createMutation.isPending || updateMutation.isPending;
  const submitError = useMemo(
    () => errorMessage(createMutation.error || updateMutation.error, ''),
    [createMutation.error, updateMutation.error],
  );

  const handleOpenCreate = () => {
    logEvent('roles_tab_open_create');
    setEditing(null);
    setEditorOpen(true);
  };

  const handleOpenEdit = (row: EmployeeRoleRow) => {
    logEvent('roles_tab_open_edit', { roleUuid: row.roleUuid });
    setEditing(row);
    setEditorOpen(true);
  };

  const handleClose = () => {
    setEditorOpen(false);
    setEditing(null);
    createMutation.reset();
    updateMutation.reset();
  };

  const handleSubmit = (input: EmployeeRoleWriteInput) => {
    const onDone = (verb: string) => () => {
      onToast?.({ type: 'success', message: `Role ${verb}.` });
      handleClose();
    };
    const onFail = (verb: string) => (err: unknown) => {
      onToast?.({ type: 'error', message: errorMessage(err, `Could not ${verb} role.`) });
    };
    if (editing?.roleUuid) {
      updateMutation.mutate(
        { roleUuid: editing.roleUuid, input },
        { onSuccess: onDone('updated'), onError: onFail('update') },
      );
    } else {
      createMutation.mutate(input, {
        onSuccess: onDone('created'),
        onError: onFail('create'),
      });
    }
  };

  const handleToggleStatus = (row: EmployeeRoleRow) => {
    const next = row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    logEvent('roles_tab_toggle_status', { roleUuid: row.roleUuid, next });
    statusMutation.mutate(
      { roleUuid: row.roleUuid, status: next },
      {
        onSuccess: (saved) =>
          onToast?.({
            type: 'success',
            message: `Role ${saved.status === 'ACTIVE' ? 'activated' : 'deactivated'}.`,
          }),
        onError: (err) =>
          onToast?.({ type: 'error', message: errorMessage(err, 'Could not update role status.') }),
      },
    );
  };

  const handleDelete = (row: EmployeeRoleRow) => {
    logEvent('roles_tab_delete_requested', { roleUuid: row.roleUuid });
    setDeleteCandidate(row);
  };

  const closeDeleteModal = () => {
    logEvent('roles_tab_delete_cancelled', { roleUuid: deleteCandidate?.roleUuid || null });
    setDeleteCandidate(null);
  };

  const confirmDelete = () => {
    if (!deleteCandidate?.roleUuid) {
      return;
    }
    logEvent('roles_tab_delete_confirmed', { roleUuid: deleteCandidate.roleUuid });
    deleteMutation.mutate(deleteCandidate.roleUuid, {
      onSuccess: () => {
        onToast?.({ type: 'success', message: 'Role deleted.' });
        setDeleteCandidate(null);
      },
      onError: (err) =>
        onToast?.({ type: 'error', message: errorMessage(err, 'Could not delete role.') }),
    });
  };

  if (!active) {
    return null;
  }

  const rows = rolesQuery.data || [];
  const linkedEmployees = roleEmployeesQuery.data || [];
  const deleteBlocked = linkedEmployees.length > 0;

  return (
    <div className="roles-tab">
      <div className="roles-tab__toolbar">
        <label className="role-editor__field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <span className="role-editor__label" style={{ margin: 0 }}>Status</span>
          <select
            className="roles-tab__filter"
            value={statusFilter}
            onChange={(e) => {
              const next = e.target.value as StatusFilter;
              logEvent('roles_tab_filter_changed', { next });
              setStatusFilter(next);
            }}
          >
            <option value="ALL">All</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </label>
        <button
          type="button"
          className="roles-tab__primary"
          onClick={handleOpenCreate}
          disabled={rolesQuery.isLoading}
        >
          New role
        </button>
      </div>

      {rolesQuery.isLoading && <div className="roles-tab__loading">Loading roles…</div>}

      {!rolesQuery.isLoading && rolesQuery.isError && (
        <div className="roles-tab__error" role="alert">
          {errorMessage(rolesQuery.error, 'Could not load roles.')}
        </div>
      )}

      {!rolesQuery.isLoading && !rolesQuery.isError && rows.length === 0 && (
        <div className="roles-tab__empty">
          No roles yet. Click <strong>New role</strong> to create one.
        </div>
      )}

      {!rolesQuery.isLoading && !rolesQuery.isError && rows.length > 0 && (
        <table className="roles-tab__table" aria-label="Employee roles">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Status</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isActive = row.status === 'ACTIVE';
              return (
                <tr key={row.roleUuid}>
                  <td><strong>{row.name}</strong></td>
                  <td>{row.description || <span style={{ opacity: 0.6 }}>—</span>}</td>
                  <td>
                    <span
                      className={`roles-tab__status roles-tab__status--${isActive ? 'active' : 'inactive'}`}
                    >
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="roles-tab__row-actions">
                      <button
                        type="button"
                        className="roles-tab__row-action"
                        onClick={() => handleOpenEdit(row)}
                        disabled={submitting || statusMutation.isPending}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="roles-tab__row-action"
                        onClick={() => handleToggleStatus(row)}
                        disabled={statusMutation.isPending}
                      >
                        {isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        className="roles-tab__row-action roles-tab__row-action--danger"
                        onClick={() => handleDelete(row)}
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <RoleEditorModal
        open={editorOpen}
        initial={editing}
        submitting={submitting}
        errorMessage={submitError}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />

      {deleteCandidate ? (
        <div className="role-editor-overlay" role="dialog" aria-modal="true" aria-label="Delete role">
          <div className="role-editor role-editor--confirm">
            <div className="role-editor__confirm-header">
              <h3 className="role-editor__title">Delete role</h3>
              <p className="role-editor__confirm-copy">
                Review the employees currently using <strong>{deleteCandidate.name}</strong> before deleting it.
              </p>
            </div>

            {roleEmployeesQuery.isLoading ? (
              <div className="roles-tab__loading">Loading employees assigned to this role…</div>
            ) : null}

            {!roleEmployeesQuery.isLoading && linkedEmployees.length > 0 ? (
              <div className="role-editor__impact-block">
                <div className="role-editor__impact-title">Employees with this role</div>
                <div className="role-editor__impact-list" role="list">
                  {linkedEmployees.map((employee) => (
                    <div key={employee.employeeUuid} className="role-editor__impact-item" role="listitem">
                      <div>
                        <div className="role-editor__impact-name">{employee.name}</div>
                        <div className="role-editor__impact-meta">
                          {employee.email || employee.phone || employee.status}
                        </div>
                      </div>
                      <span className={`roles-tab__status roles-tab__status--${employee.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                        {employee.status}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="role-editor__impact-warning" role="alert">
                  Reassign or remove these employees before deleting this role.
                </p>
              </div>
            ) : null}

            {!roleEmployeesQuery.isLoading && linkedEmployees.length === 0 ? (
              <p className="role-editor__confirm-copy">
                This role has no linked employees. Deleting it cannot be undone.
              </p>
            ) : null}

            <div className="role-editor__actions">
              <button
                type="button"
                className="role-editor__cancel"
                onClick={closeDeleteModal}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="role-editor__submit role-editor__submit--danger"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending || deleteBlocked || roleEmployeesQuery.isLoading}
              >
                {deleteBlocked ? 'Reassign employees first' : deleteMutation.isPending ? 'Deleting…' : 'Delete role'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RolesTab;
